<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseButton from '@/components/ui/BaseButton.vue'
import AppIcon from '@/components/ui/AppIcon.vue'
import { useAuth } from '@/composables/useAuth'
import { useLocale } from '@/composables/useLocale'

type ResendState = 'idle' | 'sending' | 'sent' | 'error'

const { user, isEmailVerified, resendVerification } = useAuth()
const { t } = useLocale()

const dismissed = ref(false)
const resendState = ref<ResendState>('idle')

/**
 * Shown to signed-in users whose address is still unverified, until they
 * dismiss it for the session.
 */
const isVisible = computed(
  () => Boolean(user.value) && !isEmailVerified.value && !dismissed.value
)

const statusMessage = computed(() => {
  switch (resendState.value) {
    case 'sent':
      return t('auth.verificationBanner.sentMessage')
    case 'error':
      return t('auth.verificationBanner.errorMessage')
    default:
      return t('auth.verificationBanner.message')
  }
})

const resendLabel = computed(() =>
  resendState.value === 'sending'
    ? t('auth.verificationBanner.sending')
    : t('auth.verificationBanner.resendButton')
)

async function resend(): Promise<void> {
  const email = user.value?.email

  if (!email || resendState.value === 'sending') {
    return
  }

  resendState.value = 'sending'

  try {
    await resendVerification({ email })
    resendState.value = 'sent'
  } catch {
    resendState.value = 'error'
  }
}

function dismiss(): void {
  dismissed.value = true
}
</script>

<template>
  <div
    v-if="isVisible"
    role="status"
    data-testid="email-verification-banner"
    class="tw-mb-4 tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-gap-3 tw-rounded-md tw-border tw-border-amber-200 dark:tw-border-amber-800 tw-bg-amber-50 dark:tw-bg-amber-950 tw-px-4 tw-py-3"
  >
    <AppIcon
      name="solar:letter-unread-bold"
      size="xl"
      class="tw-text-amber-600 dark:tw-text-amber-400 tw-shrink-0"
    />

    <p
      class="tw-flex-1 tw-text-sm tw-text-amber-900 dark:tw-text-amber-100"
      data-testid="email-verification-banner-message"
    >
      {{ statusMessage }}
    </p>

    <div class="tw-flex tw-items-center tw-gap-2">
      <BaseButton
        variant="warn"
        size="sm"
        :disabled="resendState === 'sending'"
        data-testid="email-verification-resend-button"
        @click="resend"
      >
        {{ resendLabel }}
      </BaseButton>
      <BaseButton
        type="button"
        variant="bare"
        class="tw-p-2 tw-rounded-md hover:tw-bg-amber-100 dark:hover:tw-bg-amber-900 tw-transition-colors"
        :aria-label="t('auth.verificationBanner.dismiss')"
        data-testid="email-verification-dismiss-button"
        @click="dismiss"
      >
        <AppIcon
          name="solar:close-circle-linear"
          size="lg"
          class="tw-text-amber-700 dark:tw-text-amber-300"
        />
      </BaseButton>
    </div>
  </div>
</template>
